import { types, flow, getParent, applySnapshot, getRoot } from 'mobx-state-tree';

const TOKEN_KEY = 'whs-v1-token';

const createEmptyUserData = () => ({
  id: null,
  name: null,
  username: null,
});

const User = types.model({
  id: types.maybeNull(types.number),
  name: types.maybeNull(types.string),
  username: types.maybeNull(types.string),
});

const LoginFormStore = types
  .model({
    username: types.maybeNull(types.string),
    password: types.maybeNull(types.string),
  })
  .volatile(self => ({
    submitting: false,
  }))
  .views(self => ({
    get root() {
      return getRoot(self);
    },
  }))
  .actions(self => ({
    setValueByElement(e) {
      const { name, type, checked, value } = e.target;
      self[name] = type === 'checkbox' ? checked : value;
    },
    submit: flow(function* (e) {
      e && e.preventDefault && e.preventDefault();

      let response;
      try {
        self.submitting = true;
        response = yield self.root.api.auth.login(self.username, self.password)

        getParent(self).loggedin(response.data);
      } catch (error) {
        self.root.ui.toast.error(error);
      }

      self.submitting = false;

      return response;
    })
  }))


const AuthStore = types
  .model('AuthStore', {
    user: types.optional(User, createEmptyUserData),
    loginForm: types.optional(LoginFormStore, {}),
  })
  .views(self => ({
    get root() {
      return getParent(self);
    },
    get isAuthenticated() {
      return self.user && !!self.user.id;
    },
    get token() {
      return self.readTokenFromLocalStorage();
  },
  }))
  .actions(self => {
    const readTokenFromLocalStorage = () => {
      return window.localStorage.getItem(TOKEN_KEY) || null;
    };

    const setToken = (token) => {
      window.localStorage.setItem(TOKEN_KEY, token)
    };

    const updateUser = (data) => {
      applySnapshot(self.user, data);
    }

    const loggedin = (data) => {
      self.setToken(data.access_token);
      updateUser(data.user);
    };

    const loggedout = () => {
      self.setToken('');
      updateUser(createEmptyUserData());
    };

    const login = flow(function* (username, password) {
      try {
        const response = yield self.root.api.auth.login(username, password)
        self.loggedin(response.data);
      } catch (error) {
        self.root.ui.toast.error(error);
      }
    });

    const loadUser = flow(function* () {
      try {
        const response = yield self.root.api.auth.user();
        updateUser(response.data.data);
      } catch (error) {
        self.root.ui.toast.error(error);
      }
    });

    const logout = flow(function* () {
      try {
        yield self.root.api.auth.logout();
        self.loggedout();
      } catch (error) {
        self.root.ui.toast.error(error);
      }
    });

    return {
      readTokenFromLocalStorage,
      setToken,
      loggedin,
      loggedout,
      login,
      loadUser,
      logout,
    };
  });

export default AuthStore;
